/**
 * Public HTTP client for shop endpoints.
 *
 * Uses plain axios without auth interceptors so it works for
 * unauthenticated visitors (no JWT, no token refresh).
 * The X-Tenant-Subdomain header is included so the API can
 * resolve the organization from the request.
 */
import axios from 'axios';
import settings from '@/settings';

const publicClient = axios.create({
    baseURL: settings.apiURL,
    timeout: 30000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add tenant subdomain header only if VITE_TENANT_SUBDOMAIN is set (localhost dev).
// In proxied environments, the Origin header handles tenant resolution automatically.
const subdomainOverride = import.meta.env.VITE_TENANT_SUBDOMAIN;
if (subdomainOverride) {
    publicClient.interceptors.request.use((config) => {
        config.headers['X-Tenant-Subdomain'] = String(subdomainOverride).trim();
        return config;
    });
}

export async function publicGet<T>(url: string): Promise<T> {
    const {data} = await publicClient.get<T>(url);
    return data;
}

export async function publicPost<T>(url: string, body?: unknown): Promise<T> {
    const {data} = await publicClient.post<T>(url, body);
    return data;
}
